import { useMemo, useState } from 'react';
import type { Charge } from '@erp/rpc';

export interface ChargeWithReversalInfo extends Charge {
  /** Whether this charge is a reversal of another charge */
  isReversal: boolean;
  /** Total amount that has been reversed from this charge */
  reversedAmount: number;
  /** Remaining effective amount after reversals */
  remainingAmount: number;
  /** Whether this charge has been fully reversed */
  isFullyReversed: boolean;
  /** Whether this charge has been partially reversed */
  isPartiallyReversed: boolean;
  /** IDs of charges that reverse this one */
  reversalChargeIds: string[];
}

export interface UseChargesWithReversalsResult {
  /** Processed charges with reversal information */
  processedCharges: ChargeWithReversalInfo[];
  /** Charges to display based on current filter state */
  visibleCharges: ChargeWithReversalInfo[];
  /** Total amount of visible charges (debits positive, credits negative) */
  total: number;
  /** Whether reversed charges are being shown */
  showReversed: boolean;
  /** Toggle function for showing/hiding reversed charges */
  toggleShowReversed: () => void;
  /** Count of charges that are reversals or fully reversed (hidden when showReversed is false) */
  hiddenReversedCount: number;
}

/**
 * Hook to process charges and compute reversal relationships.
 *
 * - Computes which charges have been fully or partially reversed
 * - Tracks remaining amounts after reversals
 * - Provides filtering to hide fully reversed charges
 * - Calculates totals based on visible charges
 */
export function useChargesWithReversals(
  charges: Charge[],
): UseChargesWithReversalsResult {
  const [showReversed, setShowReversed] = useState(false);

  const processedCharges = useMemo<ChargeWithReversalInfo[]>(() => {
    // Build a map of original charge ID -> reversal charges
    const reversalMap = new Map<string, Charge[]>();

    for (const charge of charges) {
      if (charge.reversesChargeId) {
        const existing = reversalMap.get(charge.reversesChargeId) || [];
        existing.push(charge);
        reversalMap.set(charge.reversesChargeId, existing);
      }
    }

    // Process each charge and add reversal information
    return charges.map((charge): ChargeWithReversalInfo => {
      const isReversal = !!charge.reversesChargeId;
      const reversalCharges = reversalMap.get(charge.id) || [];
      const reversalChargeIds = reversalCharges.map((r) => r.id);

      // Calculate the total amount reversed from this charge
      // Reversals should have opposite direction, so we sum their amounts
      const reversedAmount = reversalCharges.reduce((sum, reversal) => {
        return sum + (Number(reversal.amount) || 0);
      }, 0);

      const originalAmount = Number(charge.amount) || 0;

      // Remaining amount is original minus reversed (clamped to 0)
      const remainingAmount = Math.max(0, originalAmount - reversedAmount);

      // Fully reversed if remaining is 0 (or very close due to floating point)
      const isFullyReversed =
        reversalCharges.length > 0 && remainingAmount < 0.005;
      const isPartiallyReversed =
        reversalCharges.length > 0 && !isFullyReversed;

      return {
        ...charge,
        isReversal,
        reversedAmount,
        remainingAmount,
        isFullyReversed,
        isPartiallyReversed,
        reversalChargeIds,
      };
    });
  }, [charges]);

  // Filter charges based on showReversed state
  const visibleCharges = useMemo(() => {
    if (showReversed) {
      return processedCharges;
    }

    // Hide:
    // 1. Charges that are reversals (they reverse another charge)
    // 2. Charges that have been fully reversed
    return processedCharges.filter(
      (charge) => !charge.isReversal && !charge.isFullyReversed,
    );
  }, [processedCharges, showReversed]);

  // Calculate total based on visible charges
  const total = useMemo(() => {
    return visibleCharges.reduce((sum, charge) => {
      // Use remaining amount for partially reversed charges, full amount otherwise
      const effectiveAmount =
        charge.isPartiallyReversed && !showReversed
          ? charge.remainingAmount
          : Number(charge.amount) || 0;

      return charge.direction === 'CREDIT'
        ? sum - effectiveAmount
        : sum + effectiveAmount;
    }, 0);
  }, [visibleCharges, showReversed]);

  // Count hidden charges
  const hiddenReversedCount = useMemo(() => {
    return processedCharges.filter(
      (charge) => charge.isReversal || charge.isFullyReversed,
    ).length;
  }, [processedCharges]);

  const toggleShowReversed = () => setShowReversed((prev) => !prev);

  return {
    processedCharges,
    visibleCharges,
    total,
    showReversed,
    toggleShowReversed,
    hiddenReversedCount,
  };
}
